import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  CRM_API,
  CrmSupportedLookups,
  ILookup
} from '@appgen-health-crm/api-interfaces';
import { Observable } from 'rxjs';
import { shareReplay, map } from 'rxjs/operators';
import { subMinutes } from 'date-fns/esm';

const CACHE_EXPIRY_MINUTES = 5;

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  private BASE_URL: string;
  private cache: { [key: string]: { o: Observable<ILookup[]>; t: Date } } = {};

  constructor(@Inject('lookupEndpoint') endpoint, private http: HttpClient) {
    this.BASE_URL = endpoint;
  }

  getLookups(
    type: CrmSupportedLookups,
    keyword?: string,
    variant?: string,
    id?: string | string[]
  ): Observable<ILookup[]> {
    this.clearStaleCache();
    const cacheKey = this.generateCacheKey(type, keyword, variant, id);

    if (!this.cache[cacheKey]) {
      const params: { [key: string]: string } = {};
      if (keyword) params.keyword = keyword;
      if (variant) params.type = variant;
      if (id && id.length > 0)
        params.id = Array.isArray(id) ? id.join(',') : id;

      const key = type.replace(/-./g, x => x[1].toUpperCase());
      this.cache[cacheKey] = {
        o: this.http
          .get<ILookup[]>(
            this.BASE_URL + CRM_API[key].base + CRM_API[key].lookups,
            { params }
          )
          .pipe(shareReplay(1)),
        t: new Date()
      };
    }

    return this.cache[cacheKey].o;
  }

  private generateCacheKey(
    type: CrmSupportedLookups,
    keyword?: string,
    variant?: string,
    id?: string | string[]
  ): string {
    const idString = Array.isArray(id) ? id.join(',') : id;
    return `${type}-${keyword || ''}-${variant || ''}-${idString || ''}`;
  }

  clearCache() {
    this.cache = {};
  }

  private clearStaleCache() {
    const fiveMinutesAgo = subMinutes(new Date(), CACHE_EXPIRY_MINUTES);
    Object.keys(this.cache).forEach(key => {
      if (this.cache[key].t < fiveMinutesAgo) {
        delete this.cache[key];
      }
    });
  }
}
